import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  deleteTestPatientsAdminTestPatientsDeletePost,
  previewTestPatientCleanupAdminTestPatientsPreviewPost,
} from '../../services/api/sdk.gen';
import type { TestPatientCleanupPreview } from '../../services/api/types.gen';
import {
  getAgencyClinics,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { ClinicSelect } from '../clinic/ClinicSelect';

const ALL_CLINICS_VALUE = '__all__';

/**
 * Internal Admin tool: preview and hard-delete patients marked is_test_patient
 * for the current agency (all clinics or one clinic).
 */
const TestPatientCleanupSection: React.FC = () => {
  const { user, showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );

  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [clinicSelection, setClinicSelection] =
    useState<string>(ALL_CLINICS_VALUE);
  const [preview, setPreview] = useState<TestPatientCleanupPreview | null>(
    null
  );
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);

  useEffect(() => {
    if (!user?.agency?.id || !isSuperUser) return;

    let cancelled = false;
    const load = async () => {
      setIsLoadingClinics(true);
      try {
        const data = await getAgencyClinics(user.agency!.id);
        if (!cancelled) setClinics(data);
      } catch (error) {
        console.error(
          'Failed to load clinics for test patient cleanup:',
          error
        );
        if (!cancelled) {
          showError(
            'Unable to Load Clinics',
            'Something went wrong. Please try again.'
          );
        }
      } finally {
        if (!cancelled) setIsLoadingClinics(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [user?.agency?.id, isSuperUser, showError]);

  if (!isSuperUser) {
    return null;
  }

  const selectedClinicId =
    clinicSelection === ALL_CLINICS_VALUE ? null : clinicSelection;

  const handlePreview = async () => {
    setIsPreviewing(true);
    setPreview(null);
    setConfirmPhrase('');
    try {
      const { data, error } =
        await previewTestPatientCleanupAdminTestPatientsPreviewPost({
          body: { clinic_id: selectedClinicId },
          throwOnError: false,
        });
      if (error || !data) {
        throw error ?? new Error('Preview failed');
      }
      setPreview(data);
    } catch (error) {
      console.error('Failed to preview test patient cleanup:', error);
      showError(
        'Preview Failed',
        getBusinessErrorMessage(error) ??
          'Unable to load test patients. Please try again.'
      );
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleDelete = async () => {
    if (!preview) return;
    setIsDeleting(true);
    try {
      const { data, error } =
        await deleteTestPatientsAdminTestPatientsDeletePost({
          body: {
            clinic_id: selectedClinicId,
            confirmation_token: preview.confirmation_token,
            confirmation_phrase: confirmPhrase,
          },
          throwOnError: false,
        });
      if (error || !data) {
        throw error ?? new Error('Delete failed');
      }
      showSuccess(data.message);
      setPreview(null);
      setConfirmPhrase('');
    } catch (error) {
      console.error('Failed to delete test patients:', error);
      showError(
        'Delete Failed',
        getBusinessErrorMessage(error) ??
          'Unable to delete test patients. Please preview again and retry.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const phraseMatches =
    !!preview && confirmPhrase === preview.confirmation_phrase;
  const canDelete =
    !!preview && preview.patients.length > 0 && phraseMatches && !isDeleting;

  const countEntries = preview
    ? Object.entries(preview.counts).filter(([, n]) => n > 0)
    : [];

  return (
    <Card className='border-red-200'>
      <CardHeader>
        <CardTitle className='flex items-center gap-2 text-red-800'>
          <Trash2 className='h-5 w-5' />
          Delete Test Patients
        </CardTitle>
        <CardDescription>
          Permanently delete patients marked as test patients (
          <code className='text-xs'>is_test_patient</code>) in this agency, plus
          their admissions, visits, claims, and related data. Preview first,
          then type the confirmation phrase to delete.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <Alert className='border-amber-200 bg-amber-50'>
          <AlertDescription className='flex gap-2 text-amber-900'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
            This cannot be undone. Only patients with the test-patient flag are
            included — double-check the preview list before confirming.
          </AlertDescription>
        </Alert>

        <div className='flex flex-wrap items-end gap-3'>
          <div className='grid min-w-[220px] gap-2'>
            <label
              htmlFor='test-patient-clinic'
              className='text-sm font-medium'
            >
              Clinic
            </label>
            <ClinicSelect
              clinics={clinics}
              includeAllOption
              allOptionValue={ALL_CLINICS_VALUE}
              allOptionLabel='All clinics'
              value={clinicSelection}
              onChange={e => {
                setClinicSelection(e.target.value);
                setPreview(null);
                setConfirmPhrase('');
              }}
              disabled={isLoadingClinics || isPreviewing || isDeleting}
              placeholder='Select clinic'
            />
          </div>
          <Button
            type='button'
            variant='outline'
            onClick={() => void handlePreview()}
            disabled={isPreviewing || isDeleting || isLoadingClinics}
          >
            {isPreviewing ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Previewing…
              </>
            ) : (
              'Preview'
            )}
          </Button>
        </div>

        {preview && (
          <div className='space-y-4'>
            {preview.patients.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No test patients found for this selection.
              </p>
            ) : (
              <>
                <div>
                  <h3 className='mb-2 text-sm font-medium'>
                    Patients to delete ({preview.patients.length})
                  </h3>
                  <div className='max-h-64 overflow-auto rounded-md border'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>MRN</TableHead>
                          <TableHead>Clinic</TableHead>
                          <TableHead>Email</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.patients.map(patient => (
                          <TableRow key={patient.id}>
                            <TableCell>
                              {patient.last_name}, {patient.first_name}
                            </TableCell>
                            <TableCell>{patient.mrn || '—'}</TableCell>
                            <TableCell>
                              {patient.clinic_name || 'Unassigned'}
                            </TableCell>
                            <TableCell className='max-w-[200px] truncate'>
                              {patient.email || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {countEntries.length > 0 && (
                  <div>
                    <h3 className='mb-2 text-sm font-medium'>
                      Related records
                    </h3>
                    <ul className='grid gap-1 text-sm sm:grid-cols-2 md:grid-cols-3'>
                      {countEntries.map(([key, value]) => (
                        <li key={key} className='text-muted-foreground'>
                          <span className='font-medium text-foreground'>
                            {value}
                          </span>{' '}
                          {key.replace(/_/g, ' ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className='grid max-w-md gap-2'>
                  <label
                    htmlFor='test-patient-confirm'
                    className='text-sm font-medium'
                  >
                    Type{' '}
                    <span className='font-mono text-red-700'>
                      {preview.confirmation_phrase}
                    </span>{' '}
                    to confirm
                  </label>
                  <Input
                    id='test-patient-confirm'
                    inputSize='default'
                    value={confirmPhrase}
                    onChange={e => setConfirmPhrase(e.target.value)}
                    disabled={isDeleting}
                    autoComplete='off'
                  />
                </div>

                <Button
                  type='button'
                  variant='destructive'
                  onClick={() => void handleDelete()}
                  disabled={!canDelete}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className='mr-2 h-4 w-4' />
                      Permanently delete
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TestPatientCleanupSection;
